import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RejectedValue, ErrorResponse } from 'shared/interfaces/core-interfaces';
import { AuthHeader } from 'services/authHeader';
import { ChangeDepartmentModel, DepartmentModel, DepartmentProjectCreateDataResult, DepartmentProjectRequest, DepartmentSliceModel, GetDepartmentProjectQryResult } from './department-interfaces';
import { API_BASE_URL as baseUri } from 'config/env';

export const GetActiveDepartments = createAsyncThunk<DepartmentModel[], void, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/GetActiveDepartments',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/department/getallactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DepartmentModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetInactiveDepartments = createAsyncThunk<DepartmentModel[], void, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/GetInactiveDepartments',
    async (_, { rejectWithValue }) => {

        const response = await fetch(baseUri + 'api/department/getallinactive', {
            method: "GET",
            headers: AuthHeader(),
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DepartmentModel[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

export const GetDepartment = createAsyncThunk<DepartmentModel, string, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/GetDepartment',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/department/get?departmentId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as DepartmentModel;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateDepartment = createAsyncThunk<ChangeDepartmentModel, DepartmentModel, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/UpdateDepartment',
    async (department: DepartmentModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/department/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(department)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as ChangeDepartmentModel;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeactivateDepartment = createAsyncThunk<ChangeDepartmentModel, string, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/DeactivateDepartment',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/department/deactivate?departmentId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const deactivateResponse = await response.json() as ChangeDepartmentModel;
            return deactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const ReactivateDepartment = createAsyncThunk<ChangeDepartmentModel, string, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/ReactivateDepartment',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/department/reactivate?departmentId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as ChangeDepartmentModel;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const DeleteDepartment = createAsyncThunk<ChangeDepartmentModel, string, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/DeleteDepartment',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/department/delete?departmentId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const reactivateResponse = await response.json() as ChangeDepartmentModel;
            return reactivateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const CreateDepartment = createAsyncThunk<ChangeDepartmentModel, DepartmentModel, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/CreateDepartment',
    async (role: DepartmentModel, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/department/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(role)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as ChangeDepartmentModel;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetDepartmentProjectCreateData = createAsyncThunk<DepartmentProjectCreateDataResult, void, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/GetDepartmentProjectCreateData',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/departmentproject/getcreatedata', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as DepartmentProjectCreateDataResult;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetDepartmentProject = createAsyncThunk<GetDepartmentProjectQryResult, string, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/GetDepartmentProject',
    async (departmentId: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/departmentproject/get?departmentId=' + departmentId, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const getModel = await response.json() as GetDepartmentProjectQryResult;
            return getModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const CreateDepartmentProject = createAsyncThunk<GetDepartmentProjectQryResult, DepartmentProjectRequest, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/CreateDepartmentProject',
    async (department: DepartmentProjectRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/departmentproject/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(department)
        });
        if (response.status === 200) {
            const createResponse = await response.json() as GetDepartmentProjectQryResult;
            return createResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const UpdateDepartmentProject = createAsyncThunk<GetDepartmentProjectQryResult, DepartmentProjectRequest, { rejectValue: RejectedValue }>(
    'Slice/DepartmentSlice/UpdateDepartmentProject',
    async (department: DepartmentProjectRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/departmentproject/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(department)
        });
        if (response.status === 200) {
            const updateResponse = await response.json() as GetDepartmentProjectQryResult;
            return updateResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

const initialValue: DepartmentSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    message: '',
    showNotification: false,
    listOfActiveDepartments: [],
    listOfInactiveDepartments: [],
    department: null,
    reloadData: false,
    departmentProjectCreateData: null,
    departmentProject: null
}

export const DepartmentSlice = createSlice({
    name: 'DepartmentSlice',
    initialState: initialValue,
    reducers: {
        resetDepartmentSlice: (state) => {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.reloadData = false;
            state.showNotification = false;
            state.message = '';
            state.listOfActiveDepartments = [];
            state.listOfInactiveDepartments = [];
            state.department = null;
            state.departmentProjectCreateData = null;
        },
        resetDepartment: (state) => {
            state.department = null;
            state.isModalLoading = false;
            state.reloadData = false;
            state.departmentProject = null;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.reloadData = false;
        },
    },
    extraReducers(builder) {
        //GetActiveDepartments
        builder.addCase(GetActiveDepartments.fulfilled, (state, action: PayloadAction<DepartmentModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfActiveDepartments = action.payload;
        })
            .addCase(GetActiveDepartments.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetActiveDepartments.pending, state => {
                state.isLoading = true;
            })
        //GetInactiveDepartments
        builder.addCase(GetInactiveDepartments.fulfilled, (state, action: PayloadAction<DepartmentModel[]>) => {
            state.isLoading = false;
            state.message = '';
            state.listOfInactiveDepartments = action.payload;
        })
            .addCase(GetInactiveDepartments.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetInactiveDepartments.pending, state => {
                state.isLoading = true;
            })
        //GetDepartment
        builder.addCase(GetDepartment.fulfilled, (state, action: PayloadAction<DepartmentModel>) => {
            state.isModalLoading = false;
            state.message = '';
            state.department = action.payload;
        })
            .addCase(GetDepartment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDepartment.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateDepartment
        builder.addCase(UpdateDepartment.fulfilled, (state, action) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The department is successfully updated.';
            state.showNotification = true;
            state.department = action.meta.arg;
        })
            .addCase(UpdateDepartment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(UpdateDepartment.pending, state => {
                state.isModalLoading = true;
            })
        //DeactivateDepartment
        builder.addCase(DeactivateDepartment.fulfilled, (state, action: PayloadAction<ChangeDepartmentModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The department is successfully deactivated.';
            state.showNotification = true;
            state.reloadData = true;
        })
            .addCase(DeactivateDepartment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeactivateDepartment.pending, state => {
                state.isModalLoading = true;
            })
        //ReactivateDepartment
        builder.addCase(ReactivateDepartment.fulfilled, (state, action: PayloadAction<ChangeDepartmentModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The department is successfully reactivated.';
            state.showNotification = true;
            state.reloadData = true;
        })
            .addCase(ReactivateDepartment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ReactivateDepartment.pending, state => {
                state.isModalLoading = true;
            })
        //DeleteDepartment
        builder.addCase(DeleteDepartment.fulfilled, (state, action: PayloadAction<ChangeDepartmentModel>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The department is successfully deleted.';
            state.showNotification = true;
        })
            .addCase(DeleteDepartment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(DeleteDepartment.pending, state => {
                state.isModalLoading = true;
            })
        //CreateDepartment
        builder.addCase(CreateDepartment.fulfilled, (state, action) => {
            state.message = 'The department is successfully created.';
            state.statusCode = "200";
            state.isModalLoading = false;
            state.showNotification = true;
            state.department = action.meta.arg;
            state.department.id = action.payload.departmentId
        })
            .addCase(CreateDepartment.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
                state.showNotification = true;
            })
            .addCase(CreateDepartment.pending, state => {
                state.isModalLoading = true;
            })
        //GetDepartmentProjectCreateData
        builder.addCase(GetDepartmentProjectCreateData.fulfilled, (state, action: PayloadAction<DepartmentProjectCreateDataResult>) => {
            state.isLoading = false;
            state.message = '';
            state.departmentProjectCreateData = action.payload;
        })
            .addCase(GetDepartmentProjectCreateData.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDepartmentProjectCreateData.pending, state => {
                // state.isLoading = true;
            })
        //GetDepartmentProject
        builder.addCase(GetDepartmentProject.fulfilled, (state, action: PayloadAction<GetDepartmentProjectQryResult>) => {
            state.isLoading = false;
            state.message = '';
            state.departmentProject = action.payload;
        })
            .addCase(GetDepartmentProject.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetDepartmentProject.pending, state => {
                // state.isLoading = true;
            })
        //CreateDepartmentProject
        builder.addCase(CreateDepartmentProject.fulfilled, (state, action: PayloadAction<GetDepartmentProjectQryResult>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The department project is successfully created.';
            state.showNotification = true;
            state.departmentProject = action.payload;
        })
            .addCase(CreateDepartmentProject.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateDepartmentProject.pending, state => {
                state.isModalLoading = true;
            })
        //UpdateDepartmentProject
        builder.addCase(UpdateDepartmentProject.fulfilled, (state, action: PayloadAction<GetDepartmentProjectQryResult>) => {
            state.statusCode = "200";
            state.isModalLoading = false;
            state.message = 'The department project is successfully updated.';
            state.showNotification = true;
            state.departmentProject = action.payload;
        })
            .addCase(UpdateDepartmentProject.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateDepartmentProject.pending, state => {
                state.isModalLoading = true;
            })
    },
});

export const { resetDepartment, resetDepartmentSlice, resetStatusCode } = DepartmentSlice.actions;
export default DepartmentSlice.reducer;